import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from '@material-ui/core';
import {
    AiChatList,
    AiChatListItem,
    AiChatListItemButtonType,
    AiChatListItemFormat,
    AiChatListItemType,
} from '@dev/ee-components';
import { ProductType } from '@dev/ee-api/ee-api-models';
import { useDispatch, useSelector } from 'react-redux';
import { AvailableFeaturesUpselling } from '@dev/ee-modules';
import { getAiTextStore } from '@/store/aiText/actions';
import { HideDialog } from '@/store/dialog/actions';
import { getAccountProfileStore, getGeneralInfo } from '@/store/account/actions';
import { OpenUpsellingOverlay as OpenUpsellingOverlayLandingPageCreator } from '@/store/landingPageCreator/actions';
import { OpenUpsellingOverlay as OpenUpsellingOverlayTemplateEditor } from '@/store/templateEditor/actions';
import { OpenUpsellingOverlay as OpenUpsellingOverlayCheckoutCreator } from '@/store/checkoutCreator/actions';
import { AiTextItemType } from '@/store/aiText/types';
import { EditorType } from '@/services/dictionaries/EditorType';
import { useCurrentPlanStatus } from '@/services/hooks/useCurrentPlanStatus';
import { useAreNewPromptsDisabled } from '../../hooks/useAreNewPromptsDisabled';
import { AiTextModalBodyProps } from '../AiTextModalBody';

export const AiTextChatList = (props: AiTextModalBodyProps): JSX.Element => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { isBusy, list } = useSelector(getAiTextStore);
    const { IsExpired, IsPro, ProductType: productType } = useSelector(getGeneralInfo);
    const { isStandardPlan } = useCurrentPlanStatus();
    const { areNewPromptsDisabled } = useAreNewPromptsDisabled();
    const { Email, FirstName = '', LastName = '' } = useSelector(getAccountProfileStore);
    const isCreatorProduct = productType === ProductType.CreatorSuite;
    const isUpgradeable = isCreatorProduct ? !isStandardPlan : !IsPro;

    const onUpgradeClick = (): void => {
        if (props.editorType === EditorType.LandingPageCreator) {
            dispatch(OpenUpsellingOverlayLandingPageCreator(AvailableFeaturesUpselling.AiTools));
            return;
        }
        if (props.editorType === EditorType.CheckoutCreator) {
            dispatch(OpenUpsellingOverlayCheckoutCreator(AvailableFeaturesUpselling.AiTools));
            return;
        }
        dispatch(OpenUpsellingOverlayTemplateEditor(AvailableFeaturesUpselling.AiTools));
    };

    const onInsertClick = (text: string): void => {
        props.updateSelection(text, true);
        dispatch(HideDialog());
    };

    const mappedList = list.reduce((items, item): AiChatListItem[] => {
        items = [
            ...items,
            {
                id: `${item.timestamp}-request`,
                type: AiChatListItemType.REQUEST,
                content: item.type === AiTextItemType.IMPROVE ? `${t('improve')} "${item.request}"` : item.request,
                format: AiChatListItemFormat.PRELINE,
            },
        ];
        if (item.isTimeout) {
            items = [
                ...items,
                {
                    id: `${item.timestamp}-timeout`,
                    type: AiChatListItemType.CONNECTION_TIMEOUT,
                },
            ];
        } else if (item.response) {
            items = [
                ...items,
                {
                    id: `${item.timestamp}-response`,
                    type: AiChatListItemType.RESPONSE,
                    content: item.response,
                    format: AiChatListItemFormat.PRELINE,
                },
            ];
        }
        return items;
    }, []);

    return (
        <AiChatList
            errorMessages={{
                noConnection: t('aitexttool_noconnection'),
                limitReached: t('aitexttool_limitreached'),
                limitReachedWarning:
                    isUpgradeable || IsExpired ? (
                        <Trans
                            components={{ url: <Link onClick={onUpgradeClick} /> }}
                            i18nKey="aitexttool_limitreachedwarning"
                        />
                    ) : null,
            }}
            isLimitReached={areNewPromptsDisabled}
            isWaiting={isBusy}
            userName={`${FirstName} ${LastName}`}
            userEmail={Email}
            primaryResponseActions={[
                {
                    id: '1',
                    title: 'Insert into template',
                    onClick: onInsertClick,
                    type: AiChatListItemButtonType.PRIMARY,
                },
            ]}
            items={mappedList}
        />
    );
};
